import React, { useState, useRef, useEffect } from "react";
import makeStyles from "@material-ui/styles/makeStyles";
import withStyles from "@material-ui/styles/withStyles";
import Typography from "@material-ui/core/Typography";
import useTheme from "@material-ui/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import GetAppIcon from "@material-ui/icons/GetApp";
import AssessmentIcon from "@material-ui/icons/Assessment";
import { PageHeader } from "../../components";
import DateFnsUtils from "@date-io/date-fns";
import {DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import api from "../../common/api";
import CircularProgress from "@material-ui/core/CircularProgress";
import useFromValidator from '../../common/formValidator';
import {useGlobalState} from '../../state';
import { changeToLocalString, generatePdfFile } from '../../common/functions';

import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TableWrapper from "../../components/Table/TableWrapper";
import { generateReportTitle, getReportCsvHeader } from "./utils/functions";

const schema = {    
    entryDate:{
        presence: {allowEmpty: false, message: 'is required'}
    }
};

const useStyles = makeStyles(theme => ({
  root: {},
  content: props => ({
    margin: props.isDesktop ? theme.spacing(4) : theme.spacing(1)
  }),
  topBar: {
    marginBottom: theme.spacing(2)
  },
  link: {
    display: "flex"
  },
  icon: {
    marginRight: theme.spacing(0.5),
    width: 20,
    height: 20
  },
  formControl: {
    width: "100%"
  },
  progressContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%"
  },
  progressBar: {
    margin: theme.spacing(2)
  },
  singleLineBorder: {
    borderTop: "solid 1px #F26522",
    borderBottom: "solid 1px #F26522"
  },
  doubleLineBorder: {
    borderTop: "double #F26522",
    borderBottom: "double #F26522"
  },
  singleLineBorderGroup: {
   borderBottom: "solid 1px #F26522",
   borderTop: "solid 1px #F26522",
  },
  singleLineBorderGroupTop: {
     borderTop: "solid 1px #F26522",
     borderBottom: "solid 1px #F26522",
  },
  reportContainer: {},
  reportHeader: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  rawBatchName: {
    textAlign: "center",
    textDecoration: "underline"
  },
  tableHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
    stdData : {
        color:"#4999db",
        marginLeft:'1px',
        fontSize:"10px",
    }
}));

const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding:"4px",
    overflow: "hidden", 
    fontSize:"12px",
    width:"77px"  },
  body: {
    //fontSize: "1em",
    backgroundColor: theme.palette.common.white,
    padding:"4px",
    overflow: "hidden", 
    fontSize:"12px",
    width:"77px"  }
}))(TableCell);

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.background.default
    },
    "&:last-child td": {
      borderTop: "double "+ theme.palette.warning.dark,
      borderBottom: "double "+ theme.palette.warning.dark,
    }
  }
}))(TableRow);


const generateExcel = function(header, data, reportOptions, fileName="report", reportContext="") {
  let csvData = getReportCsvHeader("All Batch Daily Report", reportContext);
  header.forEach((column, index) => {
    if (index === 0) csvData = csvData + column;
    else csvData = csvData + "," + column;
  });
  csvData = csvData + "\r\n";

  // process data rows
  data.forEach(row => {
    if (row[0] === "" && row[1] !== "") {
      // Handle section header rows
      csvData = csvData + row[1] + "\r\n";
      return;
    }

    let rowData = [];
    
    // default columns (always included)
    rowData.push(row[1] || ""); // Shed
    rowData.push(row[2] || ""); // Age (in weeks)
    rowData.push(row[3] || ""); // Production %

    // good Egg %, Broken Egg %, Damaged Egg % - the server already applies the account's
    // truck eggs setting to these columns
    rowData.push(row[4] || ""); // Good Egg %
    rowData.push(row[5] || ""); // Broken Egg %
    rowData.push(row[6] || ""); // Damaged Egg %
    
    // regular columns
    rowData.push(row[7] || ""); // Feed/bird in grams
    rowData.push(row[8] || ""); // Feedin grams per EGG
    rowData.push(row[9] || ""); // Egg/bag
    rowData.push(row[10] || ""); // Mortality

    // Add Standard Data if enabled
    if (reportOptions.stdData) {
      rowData.push(row[11] || ""); // Std Egg %
      rowData.push(row[12] || ""); // Std.Feed/bird in grams
    }

    // Add Difference Data if enabled
    if (reportOptions.diffData) {
      rowData.push(row[13] || ""); // Diff. Egg Production
      rowData.push(row[14] || ""); // Diff. Feed/grams
    }
    // medicine column
    if (reportOptions.medicineView) {
      rowData.push(row[18].replace(/[\r\n]+/g, ' | ') || ""); // medicine column
    }

    // Join row data with commas
    csvData = csvData + rowData.join(",") + "\r\n";
  });
 if (window?.ReactNativeWebView) {
  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      type: "download-csv",
      name: `${fileName}.csv`,
      data: csvData,
    })
  );
 } else {

   var element = document.createElement("a");
   element.setAttribute(
     "href",
     "data:text/csv;charset=utf-8," + encodeURIComponent(csvData)
   );
   element.setAttribute("download", `${fileName}.csv`);
  
   element.style.display = "none";
   document.body.appendChild(element);
  
   element.click();
  
   document.body.removeChild(element);
 }

};

const AllBatchDaily = ({ showError, match }) => {
  const theme = useTheme();
  const [{user}] = useGlobalState();
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);
  const [data, setData] = useState([]);
    const [entryDate, setEntryDate] = useState(null);
    const [reportFileName, setReportFileName] = useState("");
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);

  const [formState, hasError, handleChange] = useFromValidator(schema);

  const [column, setColumn] = useState({
    stdData: false,
    diffData: false,
    medicineView: false,
  })

  const tableHeader = useRef(JSON.parse(`[["Shed","Age(in Weeks)","Production %","Good Egg %","Broken Egg %","Damaged Egg %",
    "Feed/bird in grams","Feedin gm/Egg","Egg/bag","Mortality","Age(in Weeks)"
  ]]`))

  useEffect(() => {
    tableHeader.current = JSON.parse(
      `[[
        "Shed","Age(in Weeks)","Production %","Good Egg %","Broken Egg %","Damaged Egg %",
        "Feed/bird in grams","Feedin gm/Egg","Egg/bag","Mortality"
        ${column.stdData ? ',"Std Egg %","Std.Feed/bird in grams"' : ''}
        ${column.diffData ? ',"Diff. Egg Production","Diff. Feed/grams"' : ''}
        ${column.medicineView ? ',"Medicine and Vaccination"' : ''}
      ]]`
    )
  }, [column]);

  const parseReportFromServer = function(data) {
    const { reportsArray } = data;
    setData(reportsArray);
    console.log(data);
  };

  const generateReport = function() {
    showError("");
    setLoading(true);
    const payload = {
      //batchID: batches[batchIndex].id
      farmID: parseInt(match.params["farmID"]),
      date: formState.values.entryDate
    };

    const response = api.post("reports/all-batch-daily", payload);

    response
      .then(res => {
        parseReportFromServer(res);

        setReportFileName(generateReportTitle({date:formState.values.entryDate, reportName: "AllBatchDaily"}));
      })
      .catch(err => {
        if (err.message) {
          if (err.message === "Bad Request" || err.message === "No data found")
            showError(err.message);
        } else {
          showError("Unknown Error");
        }
      })
      .finally(() => {
        setLoading(false);
        setEntryDate(new Date(payload.date))
      });
  };

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true
  });
  const classes = useStyles({ isDesktop });

  const generatePDF = async () => {
    if (isPdfDownloading) {
      return;
    }

    const payload = {
      farmID: parseInt(match.params["farmID"]),
      date: formState.values.entryDate,
      stdData: column.stdData,
      diffData: column.diffData,
      medicineView: column.medicineView,
    };
    setIsPdfDownloading(true);
    try {
      const pdfResponse = await generatePdfFile(
        payload,
        "/pdf/all-batch-daily",
        reportFileName,
      );
      if (!pdfResponse?.success) {
        showError(pdfResponse?.message || "Failed to download PDF");
      }
    } catch (error) {
      showError(error?.message || "Failed to download PDF");
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const amountObj = {
       /* style: "currency",
        currency: user.currency,*/
        locales: user.locales,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }

  return (
    <div className={classes.root}>
      <PageHeader
        actionsInFilterPanel
        filtersBelow
        filters={<>
      <div className="pp-filter-group">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
              autoOk
              variant="inline"
              inputVariant="outlined"
              size="small"
              label="Entry Date"
              format="dd/MM/yyyy"
              value={formState.values.entryDate || null}
              onChange={data => {
                  setValid(true);
                  handleChange({target: {name: 'entryDate', value: data}});
              }}
              onFocus={e => e.target.blur()}
              animateYearScrolling
              name="entryDate"
              error={hasError('entryDate')}
          />
        </MuiPickersUtilsProvider>
        <FormControlLabel
          control={
            <Switch
              checked={column.stdData}
              onChange={() => setColumn({
                ...column,
                stdData: !column.stdData
              })}
              color="primary"
            />
          }
          label="Std Data"
        />
        <FormControlLabel
          control={
            <Switch
              checked={column.diffData}
              onChange={() => setColumn({
                ...column,
                diffData: !column.diffData
              })}
              color="primary"
            />
          }
          label="Difference Data"
        />
        <FormControlLabel
          control={
            <Switch
              checked={column.medicineView}
              onChange={() => setColumn({
                ...column,
                medicineView: !column.medicineView
              })}
              color="primary"
            />
          }
         label="Medicine and Vaccination"
        />
      </div>
        </>}
        title="All Batch KPI Report"
        actions={[
          { label: "Generate", icon: <AssessmentIcon />, onClick: generateReport, variant: "contained", color: "primary", disabled: !valid },
          { label: isPdfDownloading ? "Downloading..." : "Download PDF", icon: isPdfDownloading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />, onClick: generatePDF, variant: "outlined", color: "primary", disabled: !valid || data.length < 1 || isPdfDownloading },
          { label: "Download Excel", icon: <GetAppIcon />, onClick: () => generateExcel(tableHeader.current, data, column, reportFileName), variant: "outlined", color: "primary", disabled: !valid || data.length < 1 },
        ]}
      />
      <div className={classes.content}>
      {loading ? (
        <div className={classes.progressContainer}>
          <CircularProgress className={classes.progressBar} />
        </div>
      ) : (
        <div id="report-container" className={classes.reportContainer}>
          <div className={classes.reportHeader}>
            <Typography
              variant="h4"
              component="h4"
              className={classes.rawBatchName}
            >
                All Batch Daily Report {entryDate !== null ? "" + (entryDate.getDate()) + "/"+(entryDate.getMonth()+1) + "/" + entryDate.getFullYear() : ""}
            </Typography>
          </div>
          <TableWrapper enableKeyboardNavigation={true} focus={data && data.length > 0}>
          <Table style={{ tableLayout: "fixed"}} stickyHeader aria-label="sticky table">
            <TableHead className={classes.tableHeader} >
              <StyledTableRow>
              <StyledTableCell>Shed</StyledTableCell>
                 <StyledTableCell>Age (in weeks)</StyledTableCell>
                <StyledTableCell>Production %</StyledTableCell>
                <StyledTableCell>Good Egg %</StyledTableCell>
                <StyledTableCell>Broken Egg %</StyledTableCell>
                <StyledTableCell>Damaged Egg %</StyledTableCell>
                <StyledTableCell>Feed/bird in grams</StyledTableCell>
                 <StyledTableCell>Feedin gm/egg</StyledTableCell>
                <StyledTableCell>Egg/bag</StyledTableCell>
                <StyledTableCell>Mortality</StyledTableCell>
                {
                  (column.diffData) && 
                  <>
                      <StyledTableCell>Diff. Egg Production</StyledTableCell>
                      <StyledTableCell>Diff. Feed/grams</StyledTableCell>
                  </>
                }
                 {
                  (column.medicineView) &&
                  <>
                    <StyledTableCell>Medicine / Vaccination</StyledTableCell>
                  </>
                }
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => {
                if (row[0] === "" && row[1] !== "" ) {
                  return(
                    <StyledTableRow key={index}>
                       <StyledTableCell className={classes.singleLineBorderGroupTop} colSpan={6} style={{ fontWeight: "bold" }} align="left">
                        {row[1] ? row[1] : ""}
                      </StyledTableCell>
                      <StyledTableCell className={classes.singleLineBorderGroupTop} colSpan={4 + (column.diffData ? 2 : 0) + (column.medicineView ? 1 : 0)} style={{ fontWeight: "bold" }} align="left">
                        Group Batch
                      </StyledTableCell>
                    </StyledTableRow>
                  )
                }else {
                  return (

                  <StyledTableRow key={index}>
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} style={{ fontWeight: "bold" }} align="left" rows={4}>
                      {row[1] ? row[1] : ""}
                    </StyledTableCell>
                 
                  <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    { row[2]}                    
                    </StyledTableCell>
                    
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    { row[3] ? changeToLocalString(row[3], amountObj) : ""}   
                      {
                       (column.stdData) && row[3]  ?

                        <span className={classes.stdData}>
                          ({row[11] ? changeToLocalString(row[11], amountObj) : ""})
                         </span> :""
                    }
                    </StyledTableCell>
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left"> 
                    {row[4] ? changeToLocalString(row[4], amountObj) : ""}
                    </StyledTableCell>
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    {row[5] ? changeToLocalString(row[5], amountObj) : ""}
                    </StyledTableCell>
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    {row[6] ? changeToLocalString(row[6], amountObj) : ""}
                    </StyledTableCell>
                    
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    { row[7] ? changeToLocalString(row[7], amountObj) : ""}          
                     {
                       (column.stdData) && row[7]  ?

                        <span className={classes.stdData}>
                          ({row[12] ? changeToLocalString(row[12], amountObj) : ""})
                         </span> :""
                    }          
                    </StyledTableCell>
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    { row[8] ? changeToLocalString(row[8], amountObj) : ""}                    
                    </StyledTableCell>
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    { row[9] ? changeToLocalString(row[9], amountObj) : ""}                    
                    </StyledTableCell>
                    <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                    { row[10] ? changeToLocalString(row[10], amountObj) : ""}                    
                    </StyledTableCell>
  
                    {
                    (column.diffData) && 
                      <>
                      <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                      { row[13] ? changeToLocalString(row[13], amountObj) : ""}                    
                      </StyledTableCell>
                      <StyledTableCell className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">
                      { row[14] ? changeToLocalString(row[14], amountObj) : ""}                    
                      </StyledTableCell>
                      </>
                    } 
                    {
                      (column.medicineView)  ?
                          <StyledTableCell  className={row[1] === "Group Total" ? classes.singleLineBorderGroup : ""} align="left">

                            <span>
                                {row[18]?.split('\n').map((item, i) => (
                                <div key={i}>{item}</div>
                              ))}
                              </span>
                         </StyledTableCell>
                         :""
                    }
                  </StyledTableRow>
                  )
                }
              })}
            </TableBody>
          </Table>
          </TableWrapper>
        </div>
      )}
      </div>
    </div>
  );
};

export default AllBatchDaily;
